import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { downloadAnalysisPdf } from "@/lib/pdf";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  ChevronLeft, Plus, Download, Trash2, Edit3, Calendar,
  FileText, Sparkles, AlertCircle, Loader2, ShoppingBag, RefreshCw, Bell,
  ClipboardList,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function ClientProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canDownloadPdf = user?.plan !== "free";
  const [client, setClient] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [sales, setSales] = useState([]);
  const [followupStatus, setFollowupStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", age: "", notes: "", telefono: "", email: "" });
  const [editPhoneError, setEditPhoneError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  // Polling state quando arriviamo da timeout di /api/analyses
  // (URL con ?awaiting=1). Cerchiamo l'apparire di una nuova analisi.
  const isAwaiting = searchParams.get("awaiting") === "1";
  const [awaitingNew, setAwaitingNew] = useState(isAwaiting);
  const baselineCountRef = useRef(null);

  const fetchAll = async () => {
    // Difensivo (mag 2026): uso allSettled in modo che il fallimento di una
    // sub-chiamata (es. /followup-status transiente) non cancelli lo state
    // già popolato della pagina.
    const [cRes, aRes, sRes, fuRes] = await Promise.allSettled([
      api.get(`/clients/${id}`),
      api.get(`/clients/${id}/analyses`),
      api.get(`/clients/${id}/sales`),
      api.get(`/clients/${id}/followup-status`),
    ]);

    if (cRes.status === "fulfilled") {
      setClient(cRes.value.data);
      setEditForm({
        name: cRes.value.data.name,
        age: cRes.value.data.age || "",
        notes: cRes.value.data.notes || "",
        telefono: cRes.value.data.telefono || "",
        email: cRes.value.data.email || "",
      });
    } else if (cRes.reason?.response?.status === 404) {
      toast.error("Cliente non trovato");
      nav("/");
      return;
    } // 5xx / rete: lasciamo lo state invariato (non cancelliamo il render precedente)

    if (aRes.status === "fulfilled") setAnalyses(aRes.value.data);
    if (sRes.status === "fulfilled") setSales(sRes.value.data.items || []);
    if (fuRes.status === "fulfilled") setFollowupStatus(fuRes.value.data);

    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  // NOTA (mag 2026): il vecchio polling `?awaiting=1` è stato rimosso perché
  // ora l'AI pipeline gira in BackgroundTasks lato backend e il polling sullo
  // status si trova direttamente su /analysis/{id} (AnalysisDetail). Se siamo
  // qui con il vecchio parametro `awaiting=1` lo puliamo silenziosamente.
  useEffect(() => {
    if (awaitingNew) {
      setAwaitingNew(false);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminato");
      nav("/");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditPhoneError("");
    let telE164 = null;
    const rawTel = (editForm.telefono || "").trim();
    if (rawTel) {
      const parsed = parsePhoneNumberFromString(rawTel, "IT");
      if (!parsed || !parsed.isValid()) {
        setEditPhoneError("Numero non valido. Usa il formato internazionale (es. +39 333 1234567).");
        return;
      }
      telE164 = parsed.number;
    }
    try {
      await api.put(`/clients/${id}`, {
        name: editForm.name.trim(),
        age: editForm.age ? parseInt(editForm.age, 10) : null,
        notes: editForm.notes.trim() || null,
        telefono: telE164,
        email: editForm.email.trim().toLowerCase() || null,
      });
      toast.success("Cliente aggiornato");
      setEditOpen(false);
      fetchAll();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const downloadPdf = (analysisId) => {
    if (downloadingId) return;
    setDownloadingId(analysisId);
    const ok = downloadAnalysisPdf(analysisId);
    if (ok) {
      toast.success("PDF scaricato");
    } else {
      toast.error("Errore nel download del PDF");
    }
    setTimeout(() => setDownloadingId(null), 1500);
  };

  const deleteAnalysis = async (analysisId) => {
    try {
      await api.delete(`/analyses/${analysisId}`);
      toast.success("Analisi eliminata");
      fetchAll();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-8 w-40 shimmer rounded-lg" />
          <div className="h-32 shimmer rounded-2xl" />
          <div className="h-64 shimmer rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-to-clients">
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-sm">Torna ai clienti</span>
      </Link>

      {/* Client Header */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-8 lg:p-10 mb-10 animate-fade-up">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Scheda cliente</p>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A1A1A]" data-testid="client-name-heading">
                {client.name}
              </h1>
              {client.needs_followup && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FAF6EE] border border-[#C5A059]/50 text-[#8A6B2E] text-xs font-medium"
                  data-testid="client-needs-followup-badge"
                  title={`Ultima analisi ${client.days_since_last_analysis} giorni fa`}
                >
                  <Bell className="w-3 h-3" />
                  Da contattare
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-[#737373]">
              {client.age && <span>{client.age} anni</span>}
              <span>Cliente dal {new Date(client.created_at).toLocaleDateString("it-IT")}</span>
              <span>{analyses.length} {analyses.length === 1 ? "analisi" : "analisi"}</span>
              {client.telefono && (
                <a
                  href={`tel:${client.telefono}`}
                  className="hover:text-[#C5A059] transition-colors"
                  data-testid="client-phone-display"
                >
                  {client.telefono}
                </a>
              )}
              {client.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="hover:text-[#C5A059] transition-colors break-all"
                  data-testid="client-email-display"
                >
                  {client.email}
                </a>
              )}
            </div>
            {client.notes && (
              <div className="mt-5 p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0]">
                <p className="text-xs uppercase tracking-wider text-[#737373] mb-1 font-medium">Note</p>
                <p className="text-sm text-[#1A1A1A] leading-relaxed">{client.notes}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <button className="h-11 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-sm font-medium flex items-center gap-2 transition-all" data-testid="edit-client-button">
                  <Edit3 className="w-4 h-4" /> Modifica
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif-display text-3xl">Modifica cliente</DialogTitle>
                  <DialogDescription className="text-sm text-[#737373]">
                    Aggiorna i dati di contatto del cliente. Il telefono in formato internazionale e l'email abilitano l'invio dell'analisi via WhatsApp ed Email.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpdate} className="space-y-4 pt-2">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Nome *</label>
                    <input
                      required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                      data-testid="edit-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Età</label>
                    <input
                      type="number" value={editForm.age} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                      data-testid="edit-age-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Telefono</label>
                    <input
                      type="tel" value={editForm.telefono}
                      onChange={(e) => { setEditForm({ ...editForm, telefono: e.target.value }); if (editPhoneError) setEditPhoneError(""); }}
                      placeholder="+39 333 1234567"
                      className={`w-full h-12 px-4 rounded-xl border bg-white focus:ring-2 outline-none ${
                        editPhoneError
                          ? "border-[#D9534F] focus:border-[#D9534F] focus:ring-[#D9534F]/20"
                          : "border-[#E5E5E0] focus:border-[#C5A059] focus:ring-[#C5A059]/20"
                      }`}
                      data-testid="edit-phone-input"
                    />
                    {editPhoneError ? (
                      <p className="text-xs text-[#D9534F] mt-1.5" data-testid="edit-phone-error">{editPhoneError}</p>
                    ) : (
                      <p className="text-xs text-[#A3A3A3] mt-1.5">Formato internazionale, abilita la condivisione su WhatsApp.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Email</label>
                    <input
                      type="email" value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="cliente@email.com"
                      className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                      data-testid="edit-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Note</label>
                    <textarea
                      rows={4} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none resize-none"
                      data-testid="edit-notes-input"
                    />
                  </div>
                  <DialogFooter>
                    <button type="submit" className="h-11 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all" data-testid="edit-save-button">
                      Salva modifiche
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="h-11 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#D9534F] hover:text-[#D9534F] text-[#737373] text-sm font-medium flex items-center gap-2 transition-all" data-testid="delete-client-button">
                  <Trash2 className="w-4 h-4" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif-display text-2xl">Eliminare il cliente?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Questa azione eliminerà anche tutte le analisi associate. L'operazione è irreversibile.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-[#D9534F] hover:bg-[#c1423e]" data-testid="delete-confirm">
                    Elimina
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {followupStatus && followupStatus.last_analysis_id && (
              <Link
                to={`/client/${id}/analysis?followup=1`}
                className="h-11 px-5 rounded-xl border border-[#C5A059] text-[#8A6B2E] hover:bg-[#FAF6EE] text-sm font-medium flex items-center gap-2 transition-all"
                data-testid="new-followup-button"
                title={
                  followupStatus.is_early
                    ? `Ultima analisi ${followupStatus.days_since_last} giorni fa — il follow-up è consigliato dopo 30 giorni.`
                    : "Esegui un follow-up per confrontare i progressi"
                }
              >
                <RefreshCw className="w-4 h-4" />
                Nuovo Follow-up
              </Link>
            )}

            <Link
              to={`/client/${id}/analysis`}
              className="h-11 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-sm font-medium flex items-center gap-2 transition-all"
              data-testid="new-analysis-button"
            >
              <Plus className="w-4 h-4" /> Nuova analisi
            </Link>
          </div>
        </div>
      </div>

      {/* Prodotti acquistati */}
      {sales.length > 0 && (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 mb-10" data-testid="purchased-products-section">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium">
              Prodotti acquistati
            </p>
            <span className="ml-auto text-xs text-[#737373]">{sales.length} totali</span>
          </div>
          <div className="divide-y divide-[#E5E5E0]">
            {sales.map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-3 gap-4"
                data-testid={`purchased-product-${i}`}
              >
                <p className="text-sm text-[#1A1A1A] font-medium">{s.product_name}</p>
                <span className="text-xs text-[#737373] shrink-0">
                  {new Date(s.created_at).toLocaleDateString("it-IT")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analyses */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-3xl text-[#1A1A1A]">Storico analisi</h2>
      </div>

      {analyses.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E5E0] p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#EAE5DF] flex items-center justify-center mb-5">
            <Sparkles className="w-6 h-6 text-[#C5A059]" />
          </div>
          <h3 className="font-serif-display text-2xl text-[#1A1A1A] mb-2">Nessuna analisi ancora</h3>
          <p className="text-[#737373] mb-6 max-w-sm mx-auto">
            Carica le prime fotografie del cuoio capelluto per generare una diagnosi AI e un protocollo di trattamento.
          </p>
          <Link
            to={`/client/${id}/analysis`}
            className="inline-flex h-11 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-sm font-medium items-center gap-2 transition-all"
            data-testid="first-analysis-button"
          >
            <Plus className="w-4 h-4" /> Avvia prima analisi
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="analyses-list">
          {analyses.map((a, idx) => (
            <div
              key={a.id}
              className="rounded-2xl border border-[#E5E5E0] bg-white p-6 hover:border-[#C5A059] transition-all animate-fade-up"
              style={{ animationDelay: `${idx * 40}ms` }}
              data-testid={`analysis-card-${a.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 text-xs text-[#737373]">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(a.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    {a.tipo_analisi === "followup" && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full font-medium bg-[#FAF6EE] text-[#8A6B2E] border border-[#C5A059]/40"
                        data-testid={`analysis-followup-badge-${a.id}`}
                      >
                        <RefreshCw className="w-3 h-3" />
                        Follow-up
                      </span>
                    )}
                    {a.livello_gravita && (
                      <span className={`text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full font-medium ${
                        a.livello_gravita === "severo" ? "bg-red-50 text-red-700" :
                        a.livello_gravita === "moderato" ? "bg-amber-50 text-amber-700" :
                        "bg-emerald-50 text-emerald-700"
                      }`}>
                        {a.livello_gravita}
                      </span>
                    )}
                    {/* INTERVENTO 3 / passo 3c — badge discreto se l'analisi
                        ha un questionario pre-analisi compilato (almeno 1
                        risposta). Non mostra il contenuto, solo la presenza. */}
                    {a.cute_questionnaire && Object.keys(a.cute_questionnaire).length > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full font-medium bg-[#FAFAFA] text-[#737373] border border-[#E5E5E0]"
                        data-testid={`analysis-questionnaire-badge-${a.id}`}
                        title="Questionario pre-analisi compilato"
                      >
                        <ClipboardList className="w-3 h-3" />
                        Questionario compilato
                      </span>
                    )}
                  </div>
                  <p className="text-[#1A1A1A] leading-relaxed line-clamp-3">{a.interpretazione_tricologica || a.diagnosi}</p>
                  {a.condizioni_rilevate?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {a.condizioni_rilevate.slice(0, 5).map((c, i) => (
                        <span key={i} className="text-xs px-3 py-1 rounded-full bg-[#FAFAFA] border border-[#E5E5E0] text-[#737373]">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/analysis/${a.id}`}
                    className="h-10 px-4 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-sm font-medium flex items-center gap-2 transition-all"
                    data-testid={`view-analysis-${a.id}`}
                  >
                    <FileText className="w-4 h-4" /> Dettagli
                  </Link>
                  {canDownloadPdf && (
                    <button
                      onClick={() => downloadPdf(a.id)}
                      disabled={downloadingId === a.id}
                      className="h-10 px-4 rounded-xl bg-[#C5A059] hover:bg-[#B38F4E] text-white text-sm font-medium flex items-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid={`download-pdf-${a.id}`}
                    >
                      {downloadingId === a.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Download className="w-4 h-4" />}
                      PDF
                    </button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="h-10 px-3 rounded-xl border border-[#E5E5E0] hover:border-[#D9534F] hover:text-[#D9534F] text-[#737373] transition-all" data-testid={`delete-analysis-${a.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare l'analisi?</AlertDialogTitle>
                        <AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteAnalysis(a.id)} className="bg-[#D9534F] hover:bg-[#c1423e]">
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
